/**
 * Turn one scan into the thing the operator is holding.
 *
 * `parseScan` decides what a string looks like; this decides what it *is*, by
 * asking Prism. Every handheld screen funnels through here, so "scan anything"
 * behaves identically on Putaway, Look Up and Consolidate.
 *
 * Two rules earn their place:
 *
 *  - A location Prism has not seen is new, not wrong. Production was
 *    backfilled from the SKUs of stock that existed on the day, so a tote that
 *    happened to be empty has a printed label and no database row. Refusing
 *    those blocked putaway to every empty tote in the building. The format
 *    check is what guards against a misread instead: a garbled decode does not
 *    survive the LIT- regex, so anything that reaches here is a real label.
 *  - An item match must be exact. The `q` filter also does a title ILIKE, so a
 *    scanned id can drag in unrelated products that merely mention it.
 */

import { ApiError } from '../../utils/api'
import { lookupShelf, searchItems } from '../../utils/warehouseApi'
import { parseScan } from '../../utils/scanCode'

const MATCH_LIMIT = 5

/**
 * @param {string} raw  Characters as they came off the scanner.
 * @returns {Promise<{kind: 'item', item: object}
 *                  |{kind: 'location', location: object}
 *                  |{kind: 'error', message: string, raw: string}>}
 */
export async function resolveScan(raw) {
    const parsed = parseScan(raw)

    if (parsed.kind === 'location') return resolveLocation(parsed)
    if (parsed.kind === 'item') return resolveItem(parsed)

    return failed(`Not a Prism barcode: ${parsed.raw || '(nothing scanned)'}`, parsed.raw)
}

async function resolveLocation(parsed) {
    try {
        return { kind: 'location', location: await lookupShelf(parsed.value) }
    } catch (error) {
        if (error instanceof ApiError && error.status === 404) {
            return { kind: 'location', location: newLocation(parsed.value) }
        }
        return failed(error.message || `Could not look up ${parsed.value}`, parsed.raw)
    }
}

/**
 * A location the operator has scanned that Prism has no row for yet.
 *
 * Described exactly as the backend will create it on the next putaway or move
 * — `large_bay`, which allocates its own slot numbers. Claiming anything else
 * would have us send a position the backend then stores verbatim, recording
 * the wrong slot for the first item into every new tote.
 */
function newLocation(shelfId) {
    return { shelf_id: shelfId, shelf_type: 'large_bay', status: 'open', is_new: true }
}

async function resolveItem(parsed) {
    let response
    try {
        response = await searchItems({ q: parsed.value, limit: MATCH_LIMIT })
    } catch (error) {
        return failed(error.message || `Could not look up ${parsed.value}`, parsed.raw)
    }

    const match = rows(response).find((item) => String(item[parsed.by]) === parsed.value)
    if (!match) return failed(`No item found for ${parsed.value}`, parsed.raw)
    return { kind: 'item', item: match }
}

/** The endpoint has returned both a bare array and a paginated object over its life. */
function rows(response) {
    if (Array.isArray(response)) return response
    return response?.items ?? []
}

function failed(message, raw) {
    return { kind: 'error', message, raw }
}
